import { format } from 'date-fns';
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  Check,
  Clock,
  Cpu,
  DollarSign,
  Download,
  MessagesSquare,
  Target,
  Trash2,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { type FormEvent, useCallback, useMemo, useState } from 'react';
import { ActionDropdown } from '../components/ActionDropdown';
import { Badge } from '../components/Badge';
import { AddMemberModal } from '../components/forms/AddMemberModal';
import { ChangeRoleModal } from '../components/forms/ChangeRoleModal';
import { ConfirmDialog } from '../components/forms/ConfirmDialog';
import { CreateInviteModal } from '../components/forms/CreateInviteModal';
import { RegisterPhoneNumberModal } from '../components/forms/RegisterPhoneNumberModal';
import { Modal } from '../components/Modal';
import { StatCard } from '../components/StatCard';
import { Tabs } from '../components/Tabs';
import {
  type OrganizationCredits,
  type OrganizationPhoneNumber,
  type OrgInvite,
  type OrgSettingsOverridesResponse,
  useAgentExecutionDetail,
  useAgentLogs,
  useAllInvites,
  useChatOverview,
  useChatRuns,
  useBillingCheckoutReadiness,
  useDeactivateOrgPhoneNumber,
  useDeleteOrganization,
  useExportOrganizationData,
  useGrantUsageCredits,
  useOrganizationDetail,
  useOrganizationPayments,
  useOrgMembers,
  useOrgPhoneNumbers,
  useOrgSettingsOverrides,
  usePurgeOrganizationData,
  useRemoveOrgMember,
  useRevokeOrgInvite,
  useUpdateOrganization,
  useUpdateOrgPhoneNumber,
  useUpdateOrgSettingsOverrides,
  useUpdateSubscription,
} from '../hooks/useAdminData';
import { formatBillingProvider } from '../lib/billing-provider';
import { formatCurrency } from '../lib/currency';
import { navigate } from '../lib/navigation';
import { subscriptionStatusBadgeVariant, subscriptionStatusLabel } from '../lib/subscriptionStatus';
import { OrganizationOnboardingTab } from './OrganizationOnboardingTab';

function planBadgeVariant(plan: string): 'default' | 'info' | 'success' | 'primary' {
  switch (plan.toLowerCase()) {
    case 'starter':
      return 'info';
    case 'growth':
      return 'success';
    case 'enterprise':
      return 'primary';
    default:
      return 'default';
  }
}

export function OrganizationDetail({ id }: { id: string }) {
  const { data, isLoading } = useOrganizationDetail(id);
  const [activeTab, setActiveTab] = useState('settings');

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-border rounded w-48 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <StatCard key={i} label="" value="" icon={Users} loading />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-text-muted">Organization not found</p>
        <button
          onClick={() => navigate('/organizations')}
          className="mt-4 text-primary hover:text-primary-hover transition-colors text-sm"
        >
          Back to Organizations
        </button>
      </div>
    );
  }

  const credits = data.subscription?.credits ?? null;

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/organizations')}
        className="flex items-center gap-2 text-sm text-text-secondary hover:text-text transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Organizations
      </button>

      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">{data.name}</h1>
        <Badge variant={planBadgeVariant(data.plan)}>{data.plan}</Badge>
        <Badge variant={subscriptionStatusBadgeVariant(data.status)}>
          {subscriptionStatusLabel(data.status, data.planExpiredAt)}
        </Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard
          label="Active Members"
          value={`${data.activeMembers}/${data.memberCount}`}
          icon={Users}
          onClick={() => setActiveTab('members')}
        />
        <StatCard
          label="Active Agents"
          value={data.activeAgents}
          icon={Bot}
          onClick={() => setActiveTab('agents')}
        />
        <StatCard label="Leads" value={data.leadCount} icon={Target} onClick={() => setActiveTab('activity')} />
        <StatCard
          label="AI Credits Used"
          value={
            credits
              ? `${credits.usedCredits.toLocaleString()} / ${credits.grantedCredits.toLocaleString()}`
              : '-'
          }
          icon={Cpu}
          onClick={() => setActiveTab('subscription')}
        />
        <StatCard
          label="Total AI Cost"
          value={`$${parseFloat(data.totalAiCost).toFixed(2)}`}
          icon={DollarSign}
          onClick={() => setActiveTab('agents')}
        />
      </div>

      <div className="bg-surface rounded-xl border border-border p-5">
        <Tabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={[
            {
              key: 'settings',
              label: 'Settings',
              content: <SettingsTab orgId={id} orgData={data} />,
            },
            { key: 'members', label: 'Members', content: <MembersTab orgId={id} /> },
            {
              key: 'invites',
              label: 'Invites',
              content: <InvitesTab orgId={id} orgName={data.name} />,
            },
            {
              key: 'subscription',
              label: 'Subscription',
              content: <SubscriptionTab orgId={id} subscription={data.subscription} />,
            },
            {
              key: 'payments',
              label: 'Payments',
              content: <PaymentsTab orgId={id} />,
            },
            {
              key: 'agents',
              label: 'Agent Config',
              content: <AgentsTab agents={data.agentConfig} />,
            },
            {
              key: 'executions',
              label: 'Executions',
              content: <ExecutionsTab orgId={id} />,
            },
            {
              key: 'chatUsage',
              label: 'Chat Usage',
              content: <ChatUsageTab orgId={id} />,
            },
            {
              key: 'phoneNumbers',
              label: 'Phone Numbers',
              content: <PhoneNumbersTab orgId={id} />,
            },
            {
              key: 'overrides',
              label: 'Overrides',
              content: <OverridesTab orgId={id} />,
            },
            {
              key: 'onboarding',
              label: 'Onboarding',
              content: <OrganizationOnboardingTab orgId={id} />,
            },
            {
              key: 'activity',
              label: 'Activity',
              content: <ActivityTab activity={data.recentActivity} />,
            },
          ]}
        />
      </div>
    </div>
  );
}

function execStatusVariant(
  status: string,
): 'success' | 'danger' | 'info' | 'warning' | 'default' {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'running':
      return 'info';
    case 'queued':
      return 'warning';
    default:
      return 'default';
  }
}

function formatUsd(value: string) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '$0.00';
  return `$${n.toFixed(n > 0 && n < 0.01 ? 4 : 2)}`;
}

function formatDuration(ms: number | null) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const MAX_ADMIN_CREDIT_GRANT = 300_000;

const EXECUTIONS_PAGE_SIZE = 20;

export function ExecutionsTab({ orgId }: { orgId: string }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<string | null>(null);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      organizationId: orgId,
      page,
      limit: EXECUTIONS_PAGE_SIZE,
    };
    if (statusFilter) p.status = statusFilter;
    return p;
  }, [orgId, page, statusFilter]);

  const { data, isLoading } = useAgentLogs(params);
  const { data: detail } = useAgentExecutionDetail(detailId);

  const executions = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / EXECUTIONS_PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">All statuses</option>
          <option value="failed">Failed</option>
          <option value="completed">Completed</option>
          <option value="running">Running</option>
          <option value="queued">Queued</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <span className="ml-auto text-xs text-text-muted">
          {total} execution{total === 1 ? '' : 's'}
        </span>
      </div>

      {isLoading ? (
        <div className="bg-surface rounded-xl border border-border p-4 animate-pulse space-y-2">
          <div className="h-4 bg-border rounded w-full" />
          <div className="h-4 bg-border rounded w-3/4" />
        </div>
      ) : executions.length === 0 ? (
        <div className="py-10 text-center text-text-muted text-sm">No executions found</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="pb-3 pr-4 font-medium">Status</th>
                <th className="pb-3 pr-4 font-medium">Agent</th>
                <th className="pb-3 pr-4 font-medium">Lead</th>
                <th className="pb-3 pr-4 font-medium">Error</th>
                <th className="pb-3 pr-4 font-medium">Cost</th>
                <th className="pb-3 pr-4 font-medium">Duration</th>
                <th className="pb-3 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((ex) => (
                <tr
                  key={ex.id}
                  onClick={() => setDetailId(ex.id)}
                  className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors cursor-pointer"
                >
                  <td className="py-2.5 pr-4">
                    <Badge variant={execStatusVariant(ex.status)}>{ex.status}</Badge>
                  </td>
                  <td className="py-2.5 pr-4 font-mono text-xs text-text">{ex.agentType}</td>
                  <td className="py-2.5 pr-4 text-text-secondary">{ex.leadName ?? '—'}</td>
                  <td className="py-2.5 pr-4 max-w-[16rem] truncate text-danger" title={ex.errorMessage ?? undefined}>
                    {ex.errorMessage ?? ''}
                  </td>
                  <td className="py-2.5 pr-4 text-text-secondary">{formatUsd(ex.costUsd)}</td>
                  <td className="py-2.5 pr-4 text-text-secondary">{formatDuration(ex.durationMs)}</td>
                  <td className="py-2.5 text-text-secondary whitespace-nowrap">
                    {format(new Date(ex.createdAt), 'MMM d, HH:mm')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 pt-3 text-sm text-text-secondary">
              <button
                type="button"
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      <Modal
        open={!!detailId}
        onClose={() => setDetailId(null)}
        title="Execution detail"
        size="lg"
      >
        {detail && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={execStatusVariant(detail.status)}>{detail.status}</Badge>
              <span className="font-mono text-sm text-text">{detail.agentType}</span>
              {detail.modelUsed && (
                <span className="text-xs text-text-muted">{detail.modelUsed}</span>
              )}
            </div>

            {detail.errorMessage && (
              <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{detail.errorMessage}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <span className="text-xs text-text-muted">Lead</span>
                <div className="text-text">{detail.leadName ?? '—'}</div>
              </div>
              <div>
                <span className="text-xs text-text-muted">Cost</span>
                <div className="text-text">{formatUsd(detail.costUsd)}</div>
              </div>
              <div>
                <span className="text-xs text-text-muted">Tokens</span>
                <div className="text-text">
                  {detail.inputTokens.toLocaleString()} in / {detail.outputTokens.toLocaleString()}{' '}
                  out
                </div>
              </div>
              <div>
                <span className="text-xs text-text-muted">Duration</span>
                <div className="text-text">{formatDuration(detail.durationMs)}</div>
              </div>
            </div>

            {detail.toolActions.length > 0 && (
              <div>
                <span className="text-xs text-text-muted">Tool actions</span>
                <div className="mt-1 space-y-2">
                  {detail.toolActions.map((tool) => (
                    <div key={tool.id} className="rounded-lg border border-border p-2.5">
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant={execStatusVariant(tool.status)}>{tool.status}</Badge>
                        <span className="font-mono text-text">{tool.toolName}</span>
                        <span className="ml-auto text-xs text-text-muted">
                          {formatDuration(tool.durationMs)}
                        </span>
                      </div>
                      {tool.errorMessage && (
                        <p className="mt-1 text-xs text-danger">{tool.errorMessage}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <span className="text-xs text-text-muted">Input</span>
              <pre className="mt-1 bg-background rounded p-3 text-xs font-mono text-text-secondary overflow-x-auto max-h-48">
                {JSON.stringify(detail.input, null, 2)}
              </pre>
            </div>
            <div>
              <span className="text-xs text-text-muted">Output</span>
              <pre className="mt-1 bg-background rounded p-3 text-xs font-mono text-text-secondary overflow-x-auto max-h-48">
                {JSON.stringify(detail.output, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function ChatUsageTab({ orgId }: { orgId: string }) {
  const { data: overview, isLoading: overviewLoading } = useChatOverview({
    organizationId: orgId,
  });
  const { data: runs, isLoading: runsLoading } = useChatRuns({
    organizationId: orgId,
    page: 1,
    limit: 10,
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Chat Runs"
          value={overview?.runCount ?? 0}
          icon={MessagesSquare}
          loading={overviewLoading}
        />
        <StatCard
          label="Tool Calls"
          value={overview?.childCount ?? 0}
          icon={Wrench}
          loading={overviewLoading}
        />
        <StatCard
          label="Avg Duration"
          value={formatDuration(overview?.avgDurationMs ?? null)}
          icon={Clock}
          loading={overviewLoading}
        />
        <StatCard
          label="Chat Surface Cost (LLM)"
          value={overview ? `$${overview.costUsd}` : '$0.00'}
          icon={DollarSign}
          loading={overviewLoading}
        />
      </div>

      {overview?.costNote && (
        <p className="text-xs text-text-muted italic px-1">{overview.costNote}</p>
      )}

      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-medium text-text-secondary">Recent Chat Runs</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-5 py-3 font-medium">Run</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Duration</th>
                <th className="px-5 py-3 font-medium">Cost</th>
                <th className="px-5 py-3 font-medium">Started</th>
              </tr>
            </thead>
            <tbody>
              {runsLoading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                    Loading…
                  </td>
                </tr>
              ) : runs?.data?.length ? (
                runs.data.map((run) => (
                  <tr key={run.id} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 text-text font-medium">{run.id}</td>
                    <td className="px-5 py-3">
                      <Badge variant={execStatusVariant(run.status)}>{run.status}</Badge>
                    </td>
                    <td className="px-5 py-3 text-text-secondary">
                      {formatDuration(run.durationMs)}
                    </td>
                    <td className="px-5 py-3 text-text-secondary">{formatUsd(run.costUsd)}</td>
                    <td className="px-5 py-3 text-text-muted text-xs whitespace-nowrap">
                      {run.startedAt
                        ? format(new Date(run.startedAt), 'MMM d, yyyy HH:mm')
                        : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                    No chat orchestrator runs for this organization
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function SettingsTab({
  orgId,
  orgData,
}: {
  orgId: string;
  orgData: {
    name: string;
    status: string;
    isActive?: boolean;
    conversationStack?: 'convai' | 'relay';
  };
}) {
  const [name, setName] = useState(orgData.name);
  const [slug, setSlug] = useState('');
  const [region, setRegion] = useState('');
  const [country, setCountry] = useState('');
  const [timezone, setTimezone] = useState('');
  const [locale, setLocale] = useState('');
  const [currency, setCurrency] = useState('');
  const initialConversationStack = orgData.conversationStack ?? 'convai';
  const [conversationStack, setConversationStack] = useState<'convai' | 'relay'>(
    initialConversationStack,
  );
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const updateOrg = useUpdateOrganization();
  const deleteOrg = useDeleteOrganization();
  const exportData = useExportOrganizationData();
  const purgeData = usePurgeOrganizationData();

  const isActive = orgData.isActive ?? orgData.status.toLowerCase() === 'active';

  function handleSave(e: FormEvent) {
    e.preventDefault();
    const payload: Record<string, unknown> = {};
    if (name !== orgData.name) payload.name = name;
    if (slug) payload.slug = slug;
    if (region) payload.region = region;
    if (country) payload.country = country;
    if (timezone) payload.timezone = timezone;
    if (locale) payload.locale = locale;
    if (currency) payload.currency = currency;
    if (conversationStack !== initialConversationStack) {
      payload.conversationStack = conversationStack;
    }

    if (Object.keys(payload).length > 0) {
      updateOrg.mutate({ id: orgId, data: payload });
    }
  }

  function handleDeactivate() {
    updateOrg.mutate(
      { id: orgId, data: { isActive: !isActive } },
      { onSuccess: () => setConfirmDeactivate(false) },
    );
  }

  function handleDelete() {
    setDeleteError(null);
    deleteOrg.mutate(orgId, {
      onSuccess: () => navigate('/organizations'),
      onError: (error: unknown) => {
        setConfirmDelete(false);
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Failed to delete organization';
        setDeleteError(message);
      },
    });
  }

  function handleExport() {
    exportData.mutate(orgId, {
      onSuccess: (data) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${orgData.name.replace(/\s+/g, '-').toLowerCase()}-export-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
    });
  }

  function handlePurge() {
    purgeData.mutate(orgId, {
      onSuccess: () => setConfirmPurge(false),
    });
  }

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-2xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Slug</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="Leave empty to keep current"
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Region</label>
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Country</label>
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Timezone</label>
          <input
            type="text"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Locale</label>
          <input
            type="text"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Currency</label>
          <input
            type="text"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label
            htmlFor="conversation-stack"
            className="block text-sm font-medium text-text-secondary mb-1"
          >
            Conversation Stack
          </label>
          <select
            id="conversation-stack"
            value={conversationStack}
            onChange={(e) => setConversationStack(e.target.value as 'convai' | 'relay')}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          >
            <option value="convai">convai</option>
            <option value="relay">relay</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={updateOrg.isPending}
          className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50"
        >
          {updateOrg.isPending ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          type="button"
          onClick={() => setConfirmDeactivate(true)}
          className="px-4 py-2 text-sm rounded-lg border border-warning text-warning hover:bg-warning/10 transition-colors"
        >
          {isActive ? 'Deactivate' : 'Reactivate'}
        </button>
      </div>

      <div className="border-t border-border pt-5 space-y-3">
        <h3 className="text-sm font-semibold text-text">Data Management</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleExport}
            disabled={exportData.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-border text-text hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            <Download size={16} />
            {exportData.isPending ? 'Exporting...' : 'Export All Data'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmPurge(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-danger text-danger hover:bg-danger/10 transition-colors"
          >
            <Trash2 size={16} />
            Purge All Data
          </button>
        </div>
        <p className="text-xs text-text-muted">
          Export downloads all organization data as JSON. Purge permanently deletes all data
          (leads, members, agents, messages, etc.) but keeps the organization record.
        </p>
      </div>

      <div className="border-t border-border pt-5">
        <h3 className="text-sm font-semibold text-danger mb-3">Danger Zone</h3>
        <button
          type="button"
          onClick={() => {
            setDeleteError(null);
            setConfirmDelete(true);
          }}
          className="px-4 py-2 text-sm rounded-lg border border-danger text-danger hover:bg-danger/10 transition-colors"
        >
          Delete Organization
        </button>
        {deleteError && (
          <p className="mt-3 text-sm text-danger bg-danger/10 border border-danger/30 rounded-lg px-4 py-3">
            {deleteError}
          </p>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeactivate}
        onClose={() => setConfirmDeactivate(false)}
        onConfirm={handleDeactivate}
        title={isActive ? 'Deactivate Organization' : 'Reactivate Organization'}
        message={
          isActive
            ? 'This will disable the organization and prevent members from logging in.'
            : 'This will re-enable the organization.'
        }
        confirmLabel={isActive ? 'Deactivate' : 'Reactivate'}
        confirmVariant={isActive ? 'danger' : 'primary'}
        loading={updateOrg.isPending}
      />
      <ConfirmDialog
        open={confirmPurge}
        onClose={() => setConfirmPurge(false)}
        onConfirm={handlePurge}
        title="Purge All Organization Data"
        message="This will permanently delete ALL data for this organization including leads, members, agents, messages, templates, and configurations. The organization record itself will be preserved. This action CANNOT be undone."
        confirmLabel="Purge All Data"
        confirmVariant="danger"
        loading={purgeData.isPending}
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete Organization"
        message="This will deactivate the organization and prevent members from accessing it. You can reactivate it later from this page."
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleteOrg.isPending}
      />
    </form>
  );
}

function MembersTab({ orgId }: { orgId: string }) {
  const { data } = useOrgMembers(orgId);
  const removeMember = useRemoveOrgMember();
  const [addOpen, setAddOpen] = useState(false);
  const [roleTarget, setRoleTarget] = useState<{
    userId: string;
    name: string;
    role: string;
  } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{ userId: string; name: string } | null>(null);

  const members = data?.data ?? [];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setAddOpen(true)}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          Add Member
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th className="pb-3 pr-4 font-medium">Name</th>
              <th className="pb-3 pr-4 font-medium">Email</th>
              <th className="pb-3 pr-4 font-medium">Role</th>
              <th className="pb-3 pr-4 font-medium">Last Active</th>
              <th className="pb-3 font-medium w-10" />
            </tr>
          </thead>
          <tbody>
            {members.length > 0 ? (
              members.map((m) => (
                <tr key={m.id} className="border-b border-border">
                  <td className="py-3 pr-4 text-text">{m.fullName}</td>
                  <td className="py-3 pr-4 text-text-secondary">{m.email}</td>
                  <td className="py-3 pr-4">
                    <Badge
                      variant={m.role === 'admin' || m.role === 'owner' ? 'primary' : 'default'}
                    >
                      {m.role}
                    </Badge>
                  </td>
                  <td className="py-3 pr-4 text-text-secondary">
                    {m.lastActiveAt
                      ? format(new Date(m.lastActiveAt), 'MMM d, yyyy HH:mm')
                      : 'Never'}
                  </td>
                  <td className="py-3">
                    <ActionDropdown
                      actions={[
                        {
                          label: 'Change Role',
                          onClick: () =>
                            setRoleTarget({ userId: m.id, name: m.fullName, role: m.role }),
                        },
                        {
                          label: 'Remove',
                          variant: 'danger',
                          onClick: () => setRemoveTarget({ userId: m.id, name: m.fullName }),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="py-8 text-center text-text-muted">
                  No members
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddMemberModal open={addOpen} onClose={() => setAddOpen(false)} orgId={orgId} />

      {roleTarget && (
        <ChangeRoleModal
          open
          onClose={() => setRoleTarget(null)}
          orgId={orgId}
          userId={roleTarget.userId}
          currentRole={roleTarget.role}
          userName={roleTarget.name}
        />
      )}

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) {
            removeMember.mutate(
              { orgId, userId: removeTarget.userId },
              { onSuccess: () => setRemoveTarget(null) },
            );
          }
        }}
        title="Remove Member"
        message={`Remove ${removeTarget?.name ?? 'this user'} from the organization?`}
        confirmLabel="Remove"
        confirmVariant="danger"
        loading={removeMember.isPending}
      />
    </div>
  );
}

function InvitesTab({ orgId, orgName }: { orgId: string; orgName: string }) {
  const { data } = useAllInvites({ organizationId: orgId });
  const revokeInvite = useRevokeOrgInvite();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<OrgInvite | null>(null);

  const invites = data?.data ?? [];

  const inviteStatusVariant = useCallback(
    (status: string): 'warning' | 'success' | 'danger' | 'default' => {
      switch (status.toLowerCase()) {
        case 'pending':
          return 'warning';
        case 'accepted':
          return 'success';
        case 'revoked':
        case 'expired':
          return 'danger';
        default:
          return 'default';
      }
    },
    [],
  );

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setCreateOpen(true)}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          Create Invite
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th className="pb-3 pr-4 font-medium">Email</th>
              <th className="pb-3 pr-4 font-medium">Role</th>
              <th className="pb-3 pr-4 font-medium">Status</th>
              <th className="pb-3 pr-4 font-medium">Expires</th>
              <th className="pb-3 font-medium w-10" />
            </tr>
          </thead>
          <tbody>
            {invites.length > 0 ? (
              invites.map((inv) => (
                <tr key={inv.id} className="border-b border-border">
                  <td className="py-3 pr-4 text-text">{inv.email}</td>
                  <td className="py-3 pr-4">
                    <Badge>{inv.role}</Badge>
                  </td>
                  <td className="py-3 pr-4">
                    <Badge variant={inviteStatusVariant(inv.status)}>{inv.status}</Badge>
                  </td>
                  <td className="py-3 pr-4 text-text-secondary">
                    {format(new Date(inv.expiresAt), 'MMM d, yyyy')}
                  </td>
                  <td className="py-3">
                    {inv.status.toLowerCase() === 'pending' && (
                      <button
                        onClick={() => setRevokeTarget(inv)}
                        className="px-2 py-1 text-xs rounded-md border border-border text-danger hover:bg-danger/10 transition-colors"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="py-8 text-center text-text-muted">
                  No invites
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateInviteModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        orgId={orgId}
        orgName={orgName}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (revokeTarget) {
            revokeInvite.mutate(revokeTarget.id, {
              onSuccess: () => setRevokeTarget(null),
            });
          }
        }}
        title="Revoke Invite"
        message={`Revoke invite for ${revokeTarget?.email ?? 'this user'}?`}
        confirmLabel="Revoke"
        confirmVariant="danger"
        loading={revokeInvite.isPending}
      />
    </div>
  );
}

export function PhoneNumbersTab({ orgId }: { orgId: string }) {
  const { data, isLoading } = useOrgPhoneNumbers(orgId);
  const deactivateNumber = useDeactivateOrgPhoneNumber(orgId);
  const updateNumber = useUpdateOrgPhoneNumber(orgId);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<OrganizationPhoneNumber | null>(null);

  const numbers = data ?? [];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setRegisterOpen(true)}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          Register Number
        </button>
      </div>

      {isLoading ? (
        <div className="bg-surface rounded-xl border border-border p-4 animate-pulse space-y-2">
          <div className="h-4 bg-border rounded w-full" />
          <div className="h-4 bg-border rounded w-3/4" />
        </div>
      ) : numbers.length === 0 ? (
        <div className="py-10 text-center text-text-muted text-sm">
          No phone numbers registered
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="pb-3 pr-4 font-medium">Phone Number</th>
                <th className="pb-3 pr-4 font-medium">Country</th>
                <th className="pb-3 pr-4 font-medium">Type</th>
                <th className="pb-3 pr-4 font-medium">Label</th>
                <th className="pb-3 pr-4 font-medium">Status</th>
                <th className="pb-3 font-medium w-32" />
              </tr>
            </thead>
            <tbody>
              {numbers.map((n) => {
                const isActive = !n.deactivatedAt;
                return (
                  <tr key={n.id} className="border-b border-border last:border-b-0">
                    <td className="py-3 pr-4 text-text font-mono">{n.phoneE164}</td>
                    <td className="py-3 pr-4 text-text-secondary">{n.country}</td>
                    <td className="py-3 pr-4 text-text-secondary">{n.phoneType}</td>
                    <td className="py-3 pr-4 text-text-secondary">{n.label ?? '—'}</td>
                    <td className="py-3 pr-4">
                      <Badge variant={isActive ? 'success' : 'default'}>
                        {isActive ? 'Active' : 'Deactivated'}
                      </Badge>
                    </td>
                    <td className="py-3 text-right">
                      {isActive ? (
                        <button
                          type="button"
                          onClick={() => setDeactivateTarget(n)}
                          className="px-2 py-1 text-xs rounded-md border border-border text-danger hover:bg-danger/10 transition-colors"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => updateNumber.mutate({ id: n.id, data: { reactivate: true } })}
                          disabled={updateNumber.isPending}
                          className="px-2 py-1 text-xs rounded-md border border-border text-success hover:bg-success/10 transition-colors disabled:opacity-50"
                        >
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <RegisterPhoneNumberModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        orgId={orgId}
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => {
          if (deactivateTarget) {
            deactivateNumber.mutate(deactivateTarget.id, {
              onSuccess: () => setDeactivateTarget(null),
            });
          }
        }}
        title="Deactivate Phone Number"
        message={`Deactivate ${deactivateTarget?.phoneE164 ?? 'this number'}? It will no longer be usable as a caller ID.`}
        confirmLabel="Deactivate Number"
        confirmVariant="danger"
        loading={deactivateNumber.isPending}
      />
    </div>
  );
}

const SUBSCRIPTION_PLANS = [
  'free',
  'starter',
  'growth',
  'professional',
  'business',
  'enterprise',
] as const;

function apiErrorMessage(error: unknown, fallback: string): string {
  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback
  );
}

function paymentStatusVariant(
  status: string,
): 'success' | 'danger' | 'warning' | 'info' | 'default' {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'paid':
    case 'succeeded':
      return 'success';
    case 'failed':
    case 'canceled':
    case 'cancelled':
      return 'danger';
    case 'pending':
    case 'past_due':
      return 'warning';
    case 'refunded':
      return 'info';
    default:
      return 'default';
  }
}

const PAYMENTS_PAGE_SIZE = 20;

export function PaymentsTab({ orgId }: { orgId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useOrganizationPayments(orgId, {
    page,
    limit: PAYMENTS_PAGE_SIZE,
  });

  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="space-y-4">
      <p className="text-xs text-text-muted">
        Subscription/billing payments for this organization — platform revenue, not the
        organization's own lead-sales.
      </p>
      <div className="bg-surface rounded-xl border border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Provider</th>
                <th className="px-5 py-3 font-medium">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                    Loading…
                  </td>
                </tr>
              ) : data?.data?.length ? (
                data.data.map((payment) => (
                  <tr key={payment.id} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 text-text-muted text-xs whitespace-nowrap">
                      {format(new Date(payment.occurredAt), 'MMM d, yyyy HH:mm')}
                    </td>
                    <td className="px-5 py-3 text-text font-medium">
                      {formatCurrency(payment.amount, payment.currencyCode)}
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={paymentStatusVariant(payment.status)}>{payment.status}</Badge>
                    </td>
                    <td className="px-5 py-3 text-text-secondary">
                      {formatBillingProvider(payment.provider)}
                    </td>
                    <td className="px-5 py-3">
                      {payment.invoiceUrl ? (
                        <a
                          href={payment.invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary-hover transition-colors"
                        >
                          Invoice
                        </a>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                    No payments recorded for this organization
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-text-muted">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SubscriptionTab({
  orgId,
  subscription,
}: {
  orgId: string;
  subscription: {
    plan: string;
    status: string;
    planExpiredAt: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    provider: string | null;
    billingCycle: 'monthly' | 'yearly' | null;
    externalCustomerId: string | null;
    externalSubscriptionId: string | null;
    cancelAtPeriodEnd: boolean;
    trialEndsAt: string | null;
    credits: OrganizationCredits | null;
  } | null;
}) {
  const updateSubscription = useUpdateSubscription();
  const grantCredits = useGrantUsageCredits();
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [grantKey, setGrantKey] = useState('');
  const { data: checkoutReadiness, isLoading: readinessLoading } =
    useBillingCheckoutReadiness(orgId);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  );

  if (!subscription) {
    return <div className="py-8 text-center text-text-muted">No subscription data</div>;
  }

  const credits = subscription.credits;
  const overAllowance = !!credits && credits.usedCredits > credits.grantedCredits;
  const usagePercent = overAllowance
    ? 100
    : credits && credits.grantedCredits > 0
      ? Math.round((credits.usedCredits / credits.grantedCredits) * 100)
      : 0;

  const status = subscription.status.toLowerCase();

  function notify(type: 'success' | 'error', message: string) {
    setFeedback({ type, message });
  }

  function changeStatus(newStatus: string, verb: string) {
    updateSubscription.mutate(
      { orgId, data: { status: newStatus } },
      {
        onSuccess: () => notify('success', `Subscription ${verb}d successfully`),
        onError: (error) => notify('error', apiErrorMessage(error, `Failed to ${verb} subscription`)),
      },
    );
  }

  const grantCreditsValue = Number(grantAmount);
  const grantValid =
    Number.isInteger(grantCreditsValue) &&
    grantCreditsValue >= 1 &&
    grantCreditsValue <= MAX_ADMIN_CREDIT_GRANT &&
    grantReason.trim().length >= 5;

  function openGrant() {
    setGrantAmount('');
    setGrantReason('');
    setGrantKey(`grant-${crypto.randomUUID()}`);
    setGrantOpen(true);
  }

  function handleGrant() {
    grantCredits.mutate(
      { orgId, credits: grantCreditsValue, reason: grantReason.trim(), idempotencyKey: grantKey },
      {
        onSuccess: (result) => {
          notify(
            'success',
            result.applied
              ? `Granted ${result.grantedCredits.toLocaleString()} credits`
              : 'This grant was already applied',
          );
          setGrantOpen(false);
        },
        onError: () => notify('error', 'Failed to grant credits'),
      },
    );
  }

  const renewsExpiredWompiPlan =
    subscription.provider === 'wompi' && subscription.planExpiredAt != null;

  function handleChangePlan() {
    updateSubscription.mutate(
      { orgId, data: { plan: selectedPlan } },
      {
        onSuccess: () => {
          notify('success', 'Plan changed successfully');
          setChangePlanOpen(false);
        },
        onError: (error) => notify('error', apiErrorMessage(error, 'Failed to change plan')),
      },
    );
  }

  return (
    <div className="space-y-5 max-w-lg">
      <div className="space-y-4">
        <div
          role="status"
          className="rounded-lg border border-border bg-background/40 px-3 py-3 flex items-center justify-between gap-3"
        >
          <div>
            <p className="text-sm font-medium text-text">
              {readinessLoading
                ? 'Checking Wompi checkout readiness'
                : checkoutReadiness?.ready
                  ? 'Wompi checkout ready'
                  : 'Wompi checkout unavailable'}
            </p>
            {checkoutReadiness?.issues.length ? (
              <p className="text-xs text-warning mt-1">
                {checkoutReadiness.issues.map((issue) => issue.replaceAll('_', ' ')).join(' · ')}
              </p>
            ) : null}
          </div>
          {checkoutReadiness?.eligible !== undefined ? (
            <Badge variant={checkoutReadiness.eligible ? 'success' : 'warning'}>
              {checkoutReadiness.eligible ? 'Eligible' : 'Not eligible'}
            </Badge>
          ) : null}
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Plan</span>
          <Badge variant={planBadgeVariant(subscription.plan)}>{subscription.plan}</Badge>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Status</span>
          <Badge variant={subscriptionStatusBadgeVariant(subscription.status)}>
            {subscriptionStatusLabel(subscription.status, subscription.planExpiredAt)}
          </Badge>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Current Period</span>
          <span className="text-text">
            {format(new Date(subscription.currentPeriodStart), 'MMM d')} -{' '}
            {format(new Date(subscription.currentPeriodEnd), 'MMM d, yyyy')}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Billing Provider</span>
          <Badge variant="default">{formatBillingProvider(subscription.provider)}</Badge>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Billing Cycle</span>
          <span className="text-text capitalize">{subscription.billingCycle ?? '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Customer ID</span>
          <span className="text-text font-mono text-xs">
            {subscription.externalCustomerId ?? '—'}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Subscription ID</span>
          <span className="text-text font-mono text-xs">
            {subscription.externalSubscriptionId ?? '—'}
          </span>
        </div>
        {subscription.trialEndsAt && (
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Trial Ends</span>
            <span className="text-text">
              {format(new Date(subscription.trialEndsAt), 'MMM d, yyyy')}
            </span>
          </div>
        )}
        {subscription.cancelAtPeriodEnd && (
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Renewal</span>
            <Badge variant="warning">Cancels at period end</Badge>
          </div>
        )}
        {credits && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">AI Credits</span>
              <span className="text-text">
                {credits.usedCredits.toLocaleString()} / {credits.grantedCredits.toLocaleString()} (
                {overAllowance ? '>100' : usagePercent}%)
              </span>
            </div>
            <div className="w-full h-2 bg-border rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  usagePercent > 90 ? 'bg-danger' : usagePercent > 70 ? 'bg-warning' : 'bg-primary'
                }`}
                style={{ width: `${Math.min(100, usagePercent)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-text-muted">
              {overAllowance && credits.availableCredits < 0 ? (
                <span className="text-danger">
                  Over by {Math.abs(credits.availableCredits).toLocaleString()} credits
                </span>
              ) : (
                <span>{credits.availableCredits.toLocaleString()} available</span>
              )}
              <span>Renews {format(new Date(credits.periodEnd), 'MMM d, yyyy')}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        <button
          type="button"
          onClick={() => {
            setSelectedPlan(subscription.plan.toLowerCase());
            setChangePlanOpen(true);
          }}
          className="px-3 py-1.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover hover:text-text transition-colors"
        >
          Change Plan
        </button>
        <button
          type="button"
          onClick={openGrant}
          className="px-3 py-1.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
        >
          Grant credits
        </button>
        {status === 'active' ? (
          <button
            type="button"
            onClick={() => changeStatus('paused', 'suspend')}
            disabled={updateSubscription.isPending}
            className="px-3 py-1.5 text-sm rounded-lg border border-warning text-warning hover:bg-warning/10 transition-colors disabled:opacity-50"
          >
            Suspend
          </button>
        ) : status === 'paused' ? (
          <button
            type="button"
            onClick={() => changeStatus('active', 'reactivate')}
            disabled={updateSubscription.isPending}
            className="px-3 py-1.5 text-sm rounded-lg border border-success text-success hover:bg-success/10 transition-colors disabled:opacity-50"
          >
            Reactivate
          </button>
        ) : null}
        {status !== 'canceled' && (
          <button
            type="button"
            onClick={() => changeStatus('canceled', 'cancel')}
            disabled={updateSubscription.isPending}
            className="px-3 py-1.5 text-sm rounded-lg border border-danger text-danger hover:bg-danger/10 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        )}
      </div>

      {feedback && (
        <p
          className={`text-sm rounded-lg px-3 py-2 border ${
            feedback.type === 'success'
              ? 'bg-success/10 border-success/30 text-success'
              : 'bg-danger/10 border-danger/30 text-danger'
          }`}
        >
          {feedback.message}
        </p>
      )}

      <Modal open={grantOpen} onClose={() => setGrantOpen(false)} title="Grant credits">
        <div className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="grant-credits" className="text-sm text-text-secondary">
              Credits
            </label>
            <input
              id="grant-credits"
              type="number"
              min={1}
              max={MAX_ADMIN_CREDIT_GRANT}
              step={1}
              value={grantAmount}
              onChange={(e) => setGrantAmount(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text"
            />
            <p className="text-xs text-text-muted">
              1 to {MAX_ADMIN_CREDIT_GRANT.toLocaleString()}. Added to the current period only.
            </p>
          </div>
          <div className="space-y-1">
            <label htmlFor="grant-reason" className="text-sm text-text-secondary">
              Reason
            </label>
            <textarea
              id="grant-reason"
              rows={3}
              maxLength={500}
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setGrantOpen(false)}
              className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleGrant}
              disabled={!grantValid || grantCredits.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-primary text-background font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {grantCredits.isPending ? 'Granting...' : 'Grant'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={changePlanOpen} onClose={() => setChangePlanOpen(false)} title="Change Plan">
        <div className="space-y-4">
          <div className="space-y-2">
            {SUBSCRIPTION_PLANS.map((plan) => (
              <label
                key={plan}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedPlan === plan
                    ? 'border-primary bg-primary-muted'
                    : 'border-border hover:border-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="plan"
                  value={plan}
                  checked={selectedPlan === plan}
                  onChange={() => setSelectedPlan(plan)}
                  className="sr-only"
                />
                <div
                  className={`w-3 h-3 rounded-full border-2 ${
                    selectedPlan === plan ? 'border-primary bg-primary' : 'border-text-muted'
                  }`}
                />
                <span className="text-sm text-text capitalize">{plan}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setChangePlanOpen(false)}
              className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleChangePlan}
              disabled={
                (selectedPlan === subscription.plan.toLowerCase() && !renewsExpiredWompiPlan) ||
                updateSubscription.isPending
              }
              className="px-4 py-2 text-sm rounded-lg bg-primary text-background font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {updateSubscription.isPending ? 'Updating...' : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function AgentsTab({
  agents,
}: {
  agents: { id: string; name: string; model: string; totalExecutions: number; totalCost: string }[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-text-muted">
            <th className="pb-3 pr-4 font-medium">Agent Name</th>
            <th className="pb-3 pr-4 font-medium">Model</th>
            <th className="pb-3 pr-4 font-medium">Executions</th>
            <th className="pb-3 font-medium">Total Cost</th>
          </tr>
        </thead>
        <tbody>
          {agents.length > 0 ? (
            agents.map((a) => (
              <tr key={a.id} className="border-b border-border">
                <td className="py-3 pr-4 text-text font-medium">{a.name}</td>
                <td className="py-3 pr-4 text-text-secondary">{a.model}</td>
                <td className="py-3 pr-4 text-text-secondary">
                  {a.totalExecutions.toLocaleString()}
                </td>
                <td className="py-3 text-text-secondary">${parseFloat(a.totalCost).toFixed(2)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className="py-8 text-center text-text-muted">
                No agents configured
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const MODEL_TIERS = ['fast', 'balanced', 'premium'];

type OverrideKey = keyof OrgSettingsOverridesResponse['overrides'];

type OverrideDraft = Record<OverrideKey, { enabled: boolean; value: number | string | boolean }>;

function buildDraft(data: OrgSettingsOverridesResponse): OverrideDraft {
  const draft = {} as OverrideDraft;
  for (const [key, field] of Object.entries(data.overrides) as [
    OverrideKey,
    { default: unknown; override: unknown; effective: unknown },
  ][]) {
    draft[key] = {
      enabled: field.override !== null,
      value: (field.override ?? field.default) as number | string | boolean,
    };
  }
  return draft;
}

function OverrideToggle({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
        enabled ? 'bg-primary' : 'bg-border'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function OverrideNumberRow({
  label,
  defaultValue,
  enabled,
  value,
  onToggle,
  onChange,
}: {
  label: string;
  defaultValue: number;
  enabled: boolean;
  value: number;
  onToggle: () => void;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex-1">
        <span className="text-sm text-text">{label}</span>
        <span className="text-xs text-text-muted ml-2">
          (Default: {defaultValue === -1 ? 'Unlimited' : defaultValue.toLocaleString()})
        </span>
      </div>
      <div className="flex items-center gap-4">
        {enabled && (
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-text w-32"
          />
        )}
        <OverrideToggle enabled={enabled} onToggle={onToggle} />
      </div>
    </div>
  );
}

function OverrideBoolRow({
  label,
  defaultValue,
  enabled,
  value,
  onToggle,
  onChange,
}: {
  label: string;
  defaultValue: boolean;
  enabled: boolean;
  value: boolean;
  onToggle: () => void;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex-1">
        <span className="text-sm text-text">{label}</span>
        <span className="text-xs text-text-muted ml-2">
          (Default: {defaultValue ? 'Enabled' : 'Disabled'})
        </span>
      </div>
      <div className="flex items-center gap-4">
        {enabled && (
          <button
            type="button"
            role="switch"
            aria-checked={value}
            onClick={() => onChange(!value)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
              value ? 'bg-green-500' : 'bg-border'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                value ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        )}
        <OverrideToggle enabled={enabled} onToggle={onToggle} />
      </div>
    </div>
  );
}

function OverrideSelectRow({
  label,
  defaultValue,
  enabled,
  value,
  options,
  onToggle,
  onChange,
}: {
  label: string;
  defaultValue: string;
  enabled: boolean;
  value: string;
  options: string[];
  onToggle: () => void;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex-1">
        <span className="text-sm text-text">{label}</span>
        <span className="text-xs text-text-muted ml-2 capitalize">
          (Default: {defaultValue})
        </span>
      </div>
      <div className="flex items-center gap-4">
        {enabled && (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-text capitalize"
          >
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )}
        <OverrideToggle enabled={enabled} onToggle={onToggle} />
      </div>
    </div>
  );
}

export function OverridesTab({ orgId }: { orgId: string }) {
  const { data, isLoading, refetch } = useOrgSettingsOverrides(orgId);
  const updateMutation = useUpdateOrgSettingsOverrides();
  const [draft, setDraft] = useState<OverrideDraft | null>(null);
  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 bg-border/30 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data) {
    return <div className="py-8 text-center text-text-muted">Failed to load settings</div>;
  }

  function startEditing() {
    setDraft(buildDraft(data!));
    setEditing(true);
  }

  function cancelEditing() {
    setDraft(null);
    setEditing(false);
  }

  function toggleField(key: OverrideKey) {
    setDraft((prev) => {
      if (!prev) return prev;
      const field = prev[key];
      return {
        ...prev,
        [key]: {
          ...field,
          enabled: !field.enabled,
          value: field.enabled ? data!.overrides[key].default : (field.value ?? data!.overrides[key].default),
        },
      };
    });
  }

  function updateField(key: OverrideKey, value: number | string | boolean) {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: { ...prev[key], value } };
    });
  }

  function handleSave() {
    if (!draft) return;
    const payload: Record<string, number | string | boolean | null> = {};

    for (const [key, field] of Object.entries(draft) as [OverrideKey, OverrideDraft[OverrideKey]][]) {
      const original = data!.overrides[key];
      if (field.enabled && original.override === null) {
        payload[key] = field.value;
      } else if (field.enabled && original.override !== null && field.value !== original.override) {
        payload[key] = field.value;
      } else if (!field.enabled && original.override !== null) {
        payload[key] = null;
      }
    }

    if (Object.keys(payload).length === 0) {
      cancelEditing();
      return;
    }

    updateMutation.mutate(
      { orgId, data: payload },
      {
        onSuccess: () => {
          setEditing(false);
          setDraft(null);
        },
      },
    );
  }

  async function reapplyMonthlyCredits() {
    const fresh = await refetch();
    const current = fresh.data?.overrides.monthlyCredits.override;
    if (current === null || current === undefined) return;
    updateMutation.mutate({ orgId, data: { monthlyCredits: current } });
  }

  const d = editing && draft ? draft : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-muted">Plan:</span>
          <Badge variant={planBadgeVariant(data.plan)}>{data.plan}</Badge>
          <span className="text-xs text-text-muted">
            Override individual settings below. Disabled overrides use the plan/global default.
          </span>
        </div>
        {!editing && (
          <button
            onClick={startEditing}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
          >
            Edit Overrides
          </button>
        )}
      </div>

      <div className="bg-surface rounded-xl border border-border p-5 space-y-1">
        <h3 className="text-sm font-medium text-text-secondary mb-2">Plan & Limits</h3>
        <OverrideNumberRow
          label="Max Leads"
          defaultValue={data.overrides.maxLeads.default}
          enabled={d ? d.maxLeads.enabled : data.overrides.maxLeads.override !== null}
          value={d ? (d.maxLeads.value as number) : data.overrides.maxLeads.effective}
          onToggle={() => d && toggleField('maxLeads')}
          onChange={(v) => updateField('maxLeads', v)}
        />
        <OverrideNumberRow
          label="Max Seats"
          defaultValue={data.overrides.maxSeats.default}
          enabled={d ? d.maxSeats.enabled : data.overrides.maxSeats.override !== null}
          value={d ? (d.maxSeats.value as number) : data.overrides.maxSeats.effective}
          onToggle={() => d && toggleField('maxSeats')}
          onChange={(v) => updateField('maxSeats', v)}
        />
        <OverrideNumberRow
          label="Prospect Contacts / Company"
          defaultValue={data.overrides.maxProspectContactsPerCompany.default}
          enabled={d ? d.maxProspectContactsPerCompany.enabled : data.overrides.maxProspectContactsPerCompany.override !== null}
          value={d ? (d.maxProspectContactsPerCompany.value as number) : data.overrides.maxProspectContactsPerCompany.effective}
          onToggle={() => d && toggleField('maxProspectContactsPerCompany')}
          onChange={(v) => updateField('maxProspectContactsPerCompany', v)}
        />
        <OverrideNumberRow
          label="Prospect Companies / Search"
          defaultValue={data.overrides.maxProspectContactCompanies.default}
          enabled={d ? d.maxProspectContactCompanies.enabled : data.overrides.maxProspectContactCompanies.override !== null}
          value={d ? (d.maxProspectContactCompanies.value as number) : data.overrides.maxProspectContactCompanies.effective}
          onToggle={() => d && toggleField('maxProspectContactCompanies')}
          onChange={(v) => updateField('maxProspectContactCompanies', v)}
        />
        <OverrideNumberRow
          label="Monthly Credits"
          defaultValue={data.overrides.monthlyCredits.default}
          enabled={d ? d.monthlyCredits.enabled : data.overrides.monthlyCredits.override !== null}
          value={d ? (d.monthlyCredits.value as number) : data.overrides.monthlyCredits.effective}
          onToggle={() => d && toggleField('monthlyCredits')}
          onChange={(v) => updateField('monthlyCredits', v)}
        />
        {!editing && data.overrides.monthlyCredits.override !== null && (
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={reapplyMonthlyCredits}
              disabled={updateMutation.isPending}
              className="text-xs font-medium text-primary hover:text-primary-hover disabled:opacity-50"
            >
              Re-apply to current period
            </button>
          </div>
        )}
      </div>

      <div className="bg-surface rounded-xl border border-border p-5 space-y-1">
        <h3 className="text-sm font-medium text-text-secondary mb-2">Features</h3>
        <OverrideSelectRow
          label="Model Tier"
          defaultValue={data.overrides.modelTier.default}
          enabled={d ? d.modelTier.enabled : data.overrides.modelTier.override !== null}
          value={d ? (d.modelTier.value as string) : data.overrides.modelTier.effective}
          options={MODEL_TIERS}
          onToggle={() => d && toggleField('modelTier')}
          onChange={(v) => updateField('modelTier', v)}
        />
        <OverrideBoolRow
          label="Voice Enabled"
          defaultValue={data.overrides.voiceEnabled.default}
          enabled={d ? d.voiceEnabled.enabled : data.overrides.voiceEnabled.override !== null}
          value={d ? (d.voiceEnabled.value as boolean) : data.overrides.voiceEnabled.effective}
          onToggle={() => d && toggleField('voiceEnabled')}
          onChange={(v) => updateField('voiceEnabled', v)}
        />
        <OverrideBoolRow
          label="WhatsApp Enabled"
          defaultValue={data.overrides.whatsappEnabled.default}
          enabled={d ? d.whatsappEnabled.enabled : data.overrides.whatsappEnabled.override !== null}
          value={d ? (d.whatsappEnabled.value as boolean) : data.overrides.whatsappEnabled.effective}
          onToggle={() => d && toggleField('whatsappEnabled')}
          onChange={(v) => updateField('whatsappEnabled', v)}
        />
        <OverrideBoolRow
          label="Custom Prompts"
          defaultValue={data.overrides.customPrompts.default}
          enabled={d ? d.customPrompts.enabled : data.overrides.customPrompts.override !== null}
          value={d ? (d.customPrompts.value as boolean) : data.overrides.customPrompts.effective}
          onToggle={() => d && toggleField('customPrompts')}
          onChange={(v) => updateField('customPrompts', v)}
        />
      </div>

      <div className="bg-surface rounded-xl border border-border p-5 space-y-1">
        <h3 className="text-sm font-medium text-text-secondary mb-2">Rate Limits & Tokens</h3>
        <OverrideNumberRow
          label="Rate Limit TTL (seconds)"
          defaultValue={data.overrides.rateLimitTtl.default}
          enabled={d ? d.rateLimitTtl.enabled : data.overrides.rateLimitTtl.override !== null}
          value={d ? (d.rateLimitTtl.value as number) : data.overrides.rateLimitTtl.effective}
          onToggle={() => d && toggleField('rateLimitTtl')}
          onChange={(v) => updateField('rateLimitTtl', v)}
        />
        <OverrideNumberRow
          label="Max Requests"
          defaultValue={data.overrides.rateLimitMax.default}
          enabled={d ? d.rateLimitMax.enabled : data.overrides.rateLimitMax.override !== null}
          value={d ? (d.rateLimitMax.value as number) : data.overrides.rateLimitMax.effective}
          onToggle={() => d && toggleField('rateLimitMax')}
          onChange={(v) => updateField('rateLimitMax', v)}
        />
        <OverrideNumberRow
          label="Monthly Token Limit"
          defaultValue={data.overrides.monthlyTokenLimit.default}
          enabled={d ? d.monthlyTokenLimit.enabled : data.overrides.monthlyTokenLimit.override !== null}
          value={d ? (d.monthlyTokenLimit.value as number) : data.overrides.monthlyTokenLimit.effective}
          onToggle={() => d && toggleField('monthlyTokenLimit')}
          onChange={(v) => updateField('monthlyTokenLimit', v)}
        />
      </div>

      {editing && (
        <div className="flex items-center gap-3 justify-end">
          <button
            onClick={cancelEditing}
            disabled={updateMutation.isPending}
            className="border border-border text-text-secondary rounded-lg px-4 py-2 text-sm hover:text-text hover:bg-surface-hover transition-colors"
          >
            <X size={14} className="inline mr-1.5" />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
          >
            <Check size={14} className="inline mr-1.5" />
            {updateMutation.isPending ? 'Saving...' : 'Save Overrides'}
          </button>
        </div>
      )}
    </div>
  );
}

function ActivityTab({
  activity,
}: {
  activity: {
    id: string;
    type: string;
    description: string;
    createdAt: string;
    userName: string | null;
  }[];
}) {
  return (
    <div className="space-y-3">
      {activity.length > 0 ? (
        activity.map((a) => (
          <div
            key={a.id}
            className="flex items-start gap-3 py-2 border-b border-border last:border-0"
          >
            <div className="flex-1">
              <p className="text-sm text-text">{a.description}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge>{a.type}</Badge>
                {a.userName && <span className="text-xs text-text-muted">by {a.userName}</span>}
              </div>
            </div>
            <span className="text-xs text-text-muted whitespace-nowrap">
              {format(new Date(a.createdAt), 'MMM d, HH:mm')}
            </span>
          </div>
        ))
      ) : (
        <div className="py-8 text-center text-text-muted">No recent activity</div>
      )}
    </div>
  );
}
